import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { images } from '../../assets';
import {
  gsap,
  setupGsap,
  prefersReducedMotion,
} from '../../motion/gsapSetup';
import { useSmoothScroll } from '../../motion/SmoothScroll';

const EMPHASIS = ['live,', 'build,', 'dwell,', 'belong,'];

const slides = [
  { image: images.projectGalleries[3]?.[0] || images.projects[0] },
  { image: images.projectGalleries[1]?.[0] || images.projects[1] },
  { image: images.projectGalleries[6]?.[0] || images.projects[2] },
];

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [wordIndex, setWordIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const slideTimer = useRef(null);
  const wordTimer = useRef(null);
  const { scrollTo } = useSmoothScroll() || {};

  useEffect(() => {
    setupGsap();
    if (prefersReducedMotion() || !sectionRef.current) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current?.children || [],
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1.15,
          stagger: 0.12,
          ease: 'power3.out',
          delay: 0.15,
        }
      );

      gsap.to(sectionRef.current.querySelectorAll('.apple-hero__slide'), {
        scale: 1.14,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      clearInterval(slideTimer.current);
      clearInterval(wordTimer.current);
      return undefined;
    }

    slideTimer.current = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % slides.length);
    }, 7000);

    wordTimer.current = setInterval(() => {
      setWordIndex((p) => (p + 1) % EMPHASIS.length);
    }, 2200);

    return () => {
      clearInterval(slideTimer.current);
      clearInterval(wordTimer.current);
    };
  }, [isPlaying]);

  const handleCta = (e) => {
    e.preventDefault();
    if (scrollTo) scrollTo('#contact');
    else document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="apple-hero" id="home" ref={sectionRef}>
      <div className="apple-hero__media" aria-hidden>
        {slides.map((s, i) => (
          <div
            key={i}
            className={`apple-hero__slide ${i === currentSlide ? 'is-active' : ''}`}
            style={{ backgroundImage: `url('${s.image}')` }}
          />
        ))}
      </div>

      <div className="apple-hero__content" ref={contentRef}>
        <p className="apple-hero__line">TheStudioVS</p>
        <h1 className="apple-hero__brand">
          The way you{' '}
          <span className="apple-italic apple-hero__word">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={EMPHASIS[wordIndex]}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {EMPHASIS[wordIndex]}
              </motion.span>
            </AnimatePresence>
          </span>
          changes now
        </h1>
        <p className="apple-hero__sub">
          Architecture, interiors, and build — under one roof. Spaces shaped with
          clarity, craftsmanship, and lasting character.
        </p>
        <a href="#contact" className="apple-cta" onClick={handleCta}>
          Get in touch
        </a>
      </div>

      <div className="apple-hero__controls">
        <motion.button
          type="button"
          whileTap={{ scale: 0.92 }}
          onClick={() => setIsPlaying((p) => !p)}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '❚❚' : '▶'}
        </motion.button>
        <div className="apple-hero__dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={i === currentSlide ? 'is-active' : ''}
              onClick={() => setCurrentSlide(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <motion.button
            type="button"
            whileTap={{ scale: 0.92 }}
            onClick={() =>
              setCurrentSlide((p) => (p - 1 + slides.length) % slides.length)
            }
            aria-label="Previous"
          >
            ‹
          </motion.button>
          <motion.button
            type="button"
            whileTap={{ scale: 0.92 }}
            onClick={() => setCurrentSlide((p) => (p + 1) % slides.length)}
            aria-label="Next"
          >
            ›
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default HeroSlider;
